import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import type { TextFieldProps } from '@mui/material/TextField';
import {
  DatePicker,
  type DatePickerProps,
  type DateValidationError,
  type PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_FORMAT_PLACEHOLDER } from '@openx/constants/date.ts';
import { CalendarBlankIcon } from '@phosphor-icons/react/CalendarBlank';
import { XIcon } from '@phosphor-icons/react/X';
import { forwardRef, type JSX, useId, useState } from 'react';
import {
  NextIconButton,
  PreviousIconButton,
  SwitchViewButton,
} from '../CalendarNavigationButtons/CalendarNavigationButtons.tsx';

export type DatePickerFieldProps = Omit<DatePickerProps, 'format'> & {
  clearable?: boolean;
  'data-test'?: string;
  error?: TextFieldProps['error'];
  helperText?: TextFieldProps['helperText'];
  label?: TextFieldProps['label'];
  margin?: TextFieldProps['margin'];
  placeholder?: TextFieldProps['placeholder'];
  sx?: FormControlProps['sx'];
};

export const DatePickerField = forwardRef<HTMLInputElement, DatePickerFieldProps>(
  (
    {
      clearable,
      'data-test': dataTest,
      error,
      helperText,
      label,
      margin = 'none',
      onChange,
      placeholder,
      sx,
      value,
      ...rest
    },
    ref
  ): JSX.Element => {
    const id = useId();
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();

      const changeContext = {
        validationError: null,
      } as PickerChangeHandlerContext<DateValidationError>;

      onChange?.(null, changeContext);
    };

    return (
      <FormControl data-test={dataTest} fullWidth margin={margin} sx={sx}>
        {label && (
          <FormLabel error={error} htmlFor={id}>
            {label}
          </FormLabel>
        )}

        <DatePicker
          format={DEFAULT_DATE_FORMAT}
          inputRef={ref}
          onChange={onChange}
          onClose={() => setIsOpen(false)}
          open={isOpen}
          slots={{
            previousIconButton: PreviousIconButton,
            nextIconButton: NextIconButton,
            switchViewButton: SwitchViewButton,
          }}
          slotProps={{
            clearButton: {
              // @ts-expect-error there is no type definition for data-* attributes on the clear button, although it works
              'data-test': 'clear-button',
            },
            field: {
              clearable,
            },
            textField: {
              error,
              helperText,
              onClick: () => setIsOpen(true),
              variant: 'outlined',
              size: 'small',

              slotProps: {
                input: {
                  slotProps: {
                    input: {
                      style: {
                        padding: '6px 0',
                      },
                    },
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      {clearable && value ? (
                        <IconButton aria-label="Clear" data-test="clear-button" onClick={handleClear} size="small">
                          <XIcon />
                        </IconButton>
                      ) : null}

                      <IconButton size="small">
                        <CalendarBlankIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                },

                htmlInput: {
                  placeholder: placeholder ?? DEFAULT_DATE_FORMAT_PLACEHOLDER,
                  // @ts-expect-error there is no type definition for data-* attributes on this slot, although it works
                  'data-test': 'date-picker',
                  id,
                },
              },
            },
          }}
          value={value}
          {...rest}
        />
      </FormControl>
    );
  }
);
